import React, { useEffect, useState } from "react";
import { get } from "../../../utils/api";
import MultiColumns from "./MultiColumns";
import { companyId } from "../../../utils/companyId";

const PageBreak = () => <div className="page-break" />;

const CompanyDetails = ({ isEditing }) => {
  const [sections, setSections] = useState([]);

  const [data, setData] = useState({
    companyInfo: null,
    basicDetails: null,
    contactDetails: null,
    locationDetails: null,
    financialInfo: null,
  });

  const fetchAllData = async () => {
    try {
      const apiCalls = [
        get(`company/companies/${companyId}`).catch((error) => {
          return { data: {} };
        }),
        get(`api/sustainabilityById/${companyId}`).catch((error) => {
          return { data: {} };
        }),
        get(`company/challenges_and_concerns/${companyId}`).catch((error) => {
          return { data: { challengesAndConcernsData: {} } };
        }),
        get(`company/future-plans/${companyId}`).catch((error) => {
          return { data: { futurePlans: {} } };
        }),
        get(`company/material-topics/${companyId}`).catch((error) => {
          return { data: { materialTopics: {} } };
        }),
      ];

      const [
        companyInfoRes,
        sustainabilityRes,
        challengesRes,
        futureRes,
        materialRes,
      ] = await Promise.all(apiCalls);

      const newData = {
        companyInfo: companyInfoRes?.data || {},
        sustainable: sustainabilityRes?.data || {},
        challenges: challengesRes?.data?.challengesAndConcernsData || {},
        future: futureRes?.data?.futurePlans || {},
        material: materialRes?.data?.materialTopics || {},
      };

      setData(newData);
      setTimeout(() => {
        console.log("Current data state:", data);
      }, 100);
    } catch (error) {
      setData({
        companyInfo: null,
        basicDetails: null,
        contactDetails: null,
        locationDetails: null,
        financialInfo: null,
      });
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [companyId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sectionConfigs = [
          {
            title: "Company Information",
            data: data?.companyInfo,
            url: "/company-information",
            questionary: [
              {
                key: "companyName",
                question: "What is your company name?",
                gridSize: { xs: 12, sm: 6 },
              },
              {
                key: "industry",
                question: "What industry are you in?",
                gridSize: { xs: 12, sm: 6 },
              },
              {
                key: "numberOfEmployees",
                question: "How many employees do you currently have?",
              },
              {
                key: "headquarters",
                question: "Where is your company headquartered?",
              },
              {
                key: "additionalLocations",
                question:
                  "Do you have additional locations? If so, please list them.",
              },
            ],
            columns: 1,
          },
          {
            title: "Sustainability",
            data: data?.sustainable,
            url: "/sustainability",
            questionary: [
              {
                key: "sustainabilityInitiatives.implementing",
                question:
                  "Are you currently implementing any sustainability initiatives or practices within your company? If yes, please provide a brief",
                // radioValue:data?.sustainable?.sustainabilityInitiatives?.implementing===""
              },

              { key: "sustainabilityInitiatives.brief", question: "Answer" },
              {
                key: "motivationForIncorporation",
                question:
                  "What motivated your company to start incorporating ?",
              },
              {
                key: "sustainabilityGoals",
                question:
                  "Are there any specific sustainability goals your company aims?",
              },
              {
                key: "primaryReasons",
                question:
                  "What are the primary reasons your company wants to incorporate sustainability into its practices?",
              },
              {
                key: "regulatoryComplianceRadio",
                question:
                  "Are there any government regulations or mandates related to sustainability that your company needs to comply with? If yes, please provide details.",
              },
              {
                key: "regulatoryCompliance",
                question: "Answer",
              },
              {
                key: "customerDemandRadio",
                question:
                  "Have your customers expressed any specific sustainability requirements or preferences that influence your company's practices? If yes, please elaborate. ",
              },
              {
                key: "customerDemand",
                question: "Answer",
              },
              //   { key: "other", question: "Can you provide an overview of your company's sustainability?" },
              {
                key: "governmentRegulationsRadio",
                question:
                  "Is there a desire within your company to become a leader in sustainability within your industry? If yes, what specific initiatives or strategies are being considered to achieve this goal?",
              },
              {
                key: "governmentRegulations",
                question: "Answer",
              },
              {
                key: "reportingStandardsRadios",
                question:
                  "Has your company selected any particular sustainability reporting standard or framework? If yes, please specify",
              },
              {
                key: "reportingStandards",
                question: "Answer",
              },
              {
                key: "sustainabilityOverview",
                question:
                  "              Can you provide an overview of your company's sustainability?",
              },
              {
                key: "progressMeasurement",
                question:
                  "How does your company plan to measure and track the progress of its sustainability initiatives?",
              },
            ],
            columns: 1,
          },
          {
            title: "challenges & concerns",
            data: data?.challenges,
            url: "/challenges-concerns",
            questionary: [
              {
                question:
                  "What do you perceive as the main challenges or barriers to implement sustainable practices within your company?",
                key: "challenges",
              },
              {
                question:
                  " Are there any specific concerns or limitations regarding sustainability initiatives that your company faces?",
                key: "concerns",
              },
            ],
            columns: 1,
          },
          {
            title: "Future Plans",
            data: data?.future,
            url: "/future-plans",
            questionary: [
              {
                question:
                  "Where do you envision your company in terms of sustainability practices in the next 5-10 years?",
                key: "sustainabilityVision",
              },
              {
                question:
                  "Are there any specific areas or aspects of sustainability that your company plans to focus on in the future?",
                key: "focusAreas",
              },
              {
                question:
                  "Is there any additional information you would like to provide regarding your company's approach to sustainability?",
                key: "additionalInfo",
              },
            ],
            columns: 1,
          },
          {
            title: "material topics",
            data: data?.material,
            url: "/material-topics",
            questionary: [
              { question: "Environment", key: "environment" },
              { question: "Social", key: "social" },
              { question: "Governance", key: "governance" },
            ],
            columns: 1,
          },
        ];

        const sectionResults = [];

        for (const config of sectionConfigs) {
          sectionResults.push({
            title: config.title,
            questionary: config.questionary,
            columns: config.columns,
            url: config.url,
            data: config?.data || {},
          });
        }

        setSections(sectionResults);
      } catch (error) {
        console.error("Error fetching section data:", error);
      }
    };

    fetchData();
  }, [data]);

  return (
    <>
      {sections.map((section, index) => {
        const needsPageBreak = [2, 4].includes(index);
        return (
          <React.Fragment key={index}>
            {needsPageBreak && (
              <>
                <PageBreak />
                <div style={{ marginTop: "1.3rem" }} />
              </>
            )}
            <MultiColumns
              key={index}
              title={section.title}
              questionary={section.questionary}
              columns={section.columns}
              url={section?.url}
              data={section?.data}
              isEditing={isEditing}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CompanyDetails;
